import { Alert, Button, Code, Group, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation } from '@tanstack/react-query';
import { rawApi } from '../api/client';
import { CrudSection } from '../components/CrudSection';
import { RejectedActions } from '../components/RejectedActions';
import { useLocationId } from '../hooks/useSeedLocation';
import { formatDateTime, formatHours, formatRelative } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';

type DeviceStatus = 'active' | 'quarantined' | 'revoked';

const count = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/** "3 sales and 1 photo" — only the parts that are actually outstanding. */
function describeUnsent(sales: number, photos: number): string | null {
  const parts: string[] = [];
  if (sales > 0) parts.push(`${sales} ${sales === 1 ? 'sale' : 'sales'}`);
  if (photos > 0) parts.push(`${photos} ${photos === 1 ? 'photo' : 'photos'}`);
  return parts.length ? parts.join(' and ') : null;
}

/** A clock this far out stamps the wrong time on an invoice, which cannot be corrected afterwards. */
const SKEW_TOLERANCE_MS = 120_000;

/**
 * Whether this device is up to date, said once.
 *
 * There were two columns of raw counters here — "Waiting to sync" and "Photos waiting" — which on
 * a healthy night are two columns of zeroes, and on a bad one are two numbers you have to add up
 * yourself. The question the owner has is not how many rows are queued, it is "is anything on that
 * device that the office has not got?", and the sentence for it already existed in this file: it was
 * being used in the retirement warning and nowhere else.
 */
function SyncCell({ row }: { row: Record<string, unknown> }) {
  const unsent = describeUnsent(count(row.pendingMutations), count(row.pendingMedia));
  const lastSync = typeof row.lastSyncAt === 'string' ? row.lastSyncAt : null;
  const skew = count(row.clockSkewMs);
  const retired = row.status === 'revoked';

  return (
    <Stack gap={0}>
      {unsent ? (
        <Text size="sm" c="orange.8" fw={500}>
          {unsent} not sent
        </Text>
      ) : (
        // "Never connected" was on every row of a healthy bar and always will be for now: devices do
        // not report back to the office yet, so this is silence, not a fault. Reading it as a fault
        // is how an owner spends a morning chasing a network problem that does not exist.
        <Text size="sm" c={lastSync ? undefined : 'dimmed'}>
          {lastSync ? 'Up to date' : 'Nothing reported yet'}
        </Text>
      )}
      {lastSync && (
        // Relative first, absolute under it: "how long has this thing been silent" is the question,
        // and a bare timestamp makes the reader do date arithmetic to answer it.
        <Text size="xs" c="dimmed">
          {formatRelative(lastSync)} · {formatDateTime(lastSync)}
        </Text>
      )}
      {/* A device whose clock is wrong stamps the wrong time on an invoice, and an invoice cannot be
          corrected once it carries a CAI and a number — only credited. Worth saying on the row. */}
      {!retired && Math.abs(skew) > SKEW_TOLERANCE_MS && (
        <Text size="xs" c="red.7">
          Its clock is {formatHours(Math.round(Math.abs(skew) / 60_000))} out
        </Text>
      )}
    </Stack>
  );
}

/**
 * The one thing on this screen that is genuinely a switch wired to a device.
 *
 * There used to be an "Approve" button here that marked a waiting device as cleared for service.
 * That was the owner's own go-ahead and it reached nothing — and now it would do worse than
 * nothing: being in service means holding a credential, and only the terminal itself can get one.
 * A row marked in service without one could never be set up at all.
 *
 * So the button hands over what the person standing at the device actually needs. The code is shown
 * once and cannot be looked up again, which is said on the modal rather than discovered.
 */
function SetUpButton({ row, refresh }: { row: Record<string, unknown>; refresh: () => void }) {
  const name = String(row.name ?? 'this device');

  const issue = useMutation({
    mutationFn: () =>
      rawApi<{ code: string }>('POST', `/admin/v1/operations/devices/${String(row.id)}/enrolment-code`, {}),
    onSuccess: (data) => {
      refresh();
      modals.open({
        title: `Setting up ${name}`,
        children: (
          <Stack gap="sm">
            <Text size="sm">Type this into the device. It works once, and only for the next hour.</Text>
            <Code block fz="xl" ta="center">
              {data.code}
            </Code>
            <Text size="sm" c="dimmed">
              Close this and it is gone — nobody can look it up again, not even you. If it is missed, come
              back here and get another one.
            </Text>
          </Stack>
        ),
      });
    },
    onError: (err) => notifyError(err, `get a set-up code for ${name}`),
  });

  return (
    <Button size="compact-xs" loading={issue.isPending} onClick={() => issue.mutate()}>
      Set it up
    </Button>
  );
}

function DeviceActions({ row, refresh }: { row: Record<string, unknown>; refresh: () => void }) {
  const name = String(row.name ?? 'this device');
  const status = String(row.status ?? '');
  const unsent = describeUnsent(count(row.pendingMutations), count(row.pendingMedia));

  const change = useMutation({
    mutationFn: ({ status: next }: { status: DeviceStatus }) =>
      rawApi('POST', `/admin/v1/operations/devices/${String(row.id)}/status`, { status: next }),
    /*
     * What these say is deliberately what actually happens — and what actually happens changed.
     * These used to be careful not to promise that anything here reached the device, because
     * nothing did. Now it does: a device is refused the moment it is put on hold or retired, at its
     * very next request. The one thing still worth saying out loud is that a tablet with no
     * internet has not heard yet, which is why retiring still asks the owner to go and collect it.
     */
    onSuccess: (_data, { status: next }) => {
      refresh();
      if (next === 'active') notifyDone('Back in service', `${name} can take orders again.`);
      else if (next === 'quarantined')
        notifyDone('Device on hold', `${name} is refused from now on. Take it off the floor.`);
      else notifyDone('Device retired', `${name} is off the list for good. Collect or wipe it.`);
    },
    onError: (err, { status: next }) =>
      notifyError(
        err,
        next === 'revoked'
          ? `retire ${name}`
          : next === 'quarantined'
            ? `put ${name} on hold`
            : `put ${name} back in service`,
      ),
  });

  // Retiring cannot be undone, and the server only blocks it while a device is still active — once
  // a device is on hold it will happily be retired with unsent sales still on it. So the warning
  // has to live here, where it is the last thing standing between the owner and lost takings.
  //
  // This used to say the tablet "keeps working until someone gets hold of it", which was true and
  // is not any more: a retired device is refused at its next request. What is still true, and is the
  // only reason to keep a sentence about the tablet at all, is that one with no internet has not
  // asked yet — so it can still be sitting in front of somebody looking perfectly normal.
  const confirmRetire = () =>
    modals.openConfirmModal({
      title: `Retire ${name}?`,
      children: (
        <Stack gap="sm">
          <Text size="sm">
            {name} comes off this list for good — it cannot be brought back, and setting it up again means
            starting from scratch with a new one on this list.
          </Text>
          <Text size="sm">
            It stops working the next time it reaches the internet. If it has no signal it will not have heard
            yet, so collect it or wipe it as well.
          </Text>
          {unsent && (
            <Alert color="red" title="This device has not finished syncing">
              <Text size="sm">
                It is still holding {unsent} that the office has never received. Retiring it now loses that
                for good. Put it on hold and give it a chance to sync first.
              </Text>
            </Alert>
          )}
        </Stack>
      ),
      labels: { confirm: 'Retire it', cancel: 'Keep it' },
      confirmProps: { color: 'red' },
      onConfirm: () => change.mutate({ status: 'revoked' }),
    });

  if (status === 'revoked') return null;

  return (
    <Group gap={4} wrap="nowrap">
      {status === 'pending' && <SetUpButton row={row} refresh={refresh} />}
      {/* Without this a device put on hold had only one way out — retirement — even when all it
          needed was time to catch up. */}
      {status === 'quarantined' && (
        <Button
          size="compact-xs"
          variant="light"
          loading={change.isPending}
          onClick={() => change.mutate({ status: 'active' })}
        >
          Put back in service
        </Button>
      )}
      {status === 'active' && (
        <Button
          size="compact-xs"
          color="orange"
          variant="light"
          loading={change.isPending}
          onClick={() => change.mutate({ status: 'quarantined' })}
        >
          Put on hold
        </Button>
      )}
      <Button size="compact-xs" color="red" variant="light" onClick={confirmRetire}>
        Retire
      </Button>
    </Group>
  );
}

/** The app version is what you read out to whoever is helping you, not something you decide on. */
function DeviceCell({ row }: { row: Record<string, unknown> }) {
  const version = row.appVersion ? String(row.appVersion) : null;
  return (
    <div>
      <Text size="sm" fw={500}>
        {String(row.name ?? '—')}
      </Text>
      {version && (
        <Text size="xs" c="dimmed">
          App {version}
        </Text>
      )}
    </div>
  );
}

export function DevicesPage() {
  const locationId = useLocationId();

  return (
    <>
      <Title order={3} mb="md">
        Devices
      </Title>
      <CrudSection
        title="Devices"
        entityNoun="device"
        /*
         * A DEVICE IS A DEVICE. It is not a register and it is not a kitchen screen.
         *
         * This screen used to ask, when a tablet was added, whether it was "for" taking orders or for
         * the kitchen — and then which cash drawer it counted against. Neither is a property of the
         * tablet. The same tablet shows the register to a bartender and the kitchen board to a cook,
         * because what somebody sees is decided by what they are allowed to do, not by a choice made
         * in the office weeks earlier by whoever unboxed it.
         *
         * So adding a device asks for a name and nothing else, and everything else about it —
         * what it is, what it is running, which build it is on — the device says for itself.
         */
        description="Every tablet used here. Add one, set it up to get the code you type into it, and it reports what it is when it connects. Put one on hold while you look into it, and retire anything lost or replaced — a device on hold or retired is refused the next time it reaches the internet."
        listPath="/admin/v1/operations/devices"
        updatePathTemplate="/admin/v1/operations/devices/{id}"
        defaults={{ locationId }}
        rowLabel={(r) => String(r.name ?? 'this device')}
        // A device cannot put itself on this list: nothing creates one except this screen, so the
        // old "it appears the first time it connects" left an owner with a new tablet waiting for
        // a row that was never going to arrive.
        emptyMessage="No devices yet. Add each one here, then set it up to get the code you type into it."
        columns={[
          { accessor: 'name', title: 'Device', render: (r) => <DeviceCell row={r} /> },
          { accessor: 'status', title: 'Status', enumGroup: 'deviceStatus' },
          {
            accessor: 'kdsReminderEnabled',
            title: 'Spoken KDS reminder',
            render: (r) =>
              r.kdsReminderEnabled
                ? `${r.kdsStationId === 'bar' ? 'Bar' : 'Kitchen'} · ${r.kdsReminderLanguage === 'en' ? 'English' : 'Spanish'}`
                : 'Off',
          },
          // "Sync" is our word for it, not his. What the column answers is when the device last told
          // the office anything, and whether it is still sitting on takings nobody has seen.
          { accessor: 'lastSyncAt', title: 'Last reported', render: (r) => <SyncCell row={r} /> },
        ]}
        fields={[
          {
            name: 'kdsReminderEnabled',
            label: 'Enable spoken KDS reminders',
            type: 'boolean',
            defaultValue: false,
            description:
              'Enable only on a KDS terminal. The voice repeats every 15 seconds until the order dialog is acknowledged. Further orders stay quiet until the station clears completely and receives new work. Works before staff sign-in. On Android, complete the device’s background and notification setup for locked-screen monitoring. On iPad/iPhone, keep the app on screen for new arrivals.',
            validate: (value, values) =>
              value && !values.kdsStationId ? 'Choose the station this terminal should monitor.' : null,
          },
          {
            name: 'kdsStationId',
            label: 'Station for spoken reminders',
            type: 'select',
            nullable: true,
            options: [
              { value: 'kitchen', label: 'Kitchen' },
              { value: 'bar', label: 'Bar' },
            ],
          },
          {
            name: 'kdsReminderLanguage',
            label: 'Spoken reminder language',
            type: 'select',
            required: true,
            defaultValue: 'es-HN',
            description:
              'Independent of the signed-in staff member’s screen language. Uses a recorded natural AI voice.',
            options: [
              { value: 'es-HN', label: 'Spanish' },
              { value: 'en', label: 'English' },
            ],
          },
          {
            name: 'name',
            label: 'What you call it',
            type: 'text',
            required: true,
            description: 'The name you know it by — "Deco Stop 1"',
          },
          {
            name: 'defaultLanguage',
            label: 'Language before sign-in',
            type: 'select',
            nullable: true,
            placeholder: 'Follow the tablet',
            description:
              'Used on the PIN screen. Leave blank to follow the tablet. After sign-in, the staff member’s own language wins.',
            options: [
              { value: 'es-HN', label: 'Spanish (Honduras)' },
              { value: 'en', label: 'English' },
            ],
          },
        ]}
        rowActions={(row, refresh) => <DeviceActions row={row} refresh={refresh} />}
      />
      {/* Under the table rather than on a screen of its own: the "not sent" sentence on a device
          row above and this list are the same table read two ways, and the retire warning is
          about these rows. See the component for why there is no way to apply one from here. */}
      <RejectedActions />
    </>
  );
}
